/**
 * DispatchPlansDropdown — Badge + dropdown con planes de distribución pendientes
 *
 * Fase 2 feb/2026: el scheduler detecta emails de Delicatto con adjuntos o
 * links musicales y los marca como "pending_review". Este badge los lista,
 * y al hacer click en uno se abre el EmailAIDispatchDialog con el email.
 */

import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Mail, Inbox, RefreshCw, Loader2, X, Paperclip, Music } from 'lucide-react';
import { API_URL } from '../config';
import { EmailAIDispatchDialog } from './EmailAIDispatchDialog';

export const DispatchPlansDropdown = ({ onPlanApplied }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [plans, setPlans] = useState([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [selectedPlan, setSelectedPlan] = useState(null);

  const loadPlans = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.get(`${API_URL}/emails/dispatch-plans?status=pending_review`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPlans(data.plans || []);
      setPendingCount(data.pending_count || 0);
    } catch (e) {
      // Log para debug pero no mostrar toast (podría ser spam al montar sin token)
      console.warn('[DispatchPlans] load failed:', e?.message || e);
    } finally {
      setLoading(false);
    }
  }, []);

  // Carga inicial + refresco:
  //  - cada 60s mientras el dropdown está abierto (UX interactiva)
  //  - cada 5 min en background cuando está cerrado (para que el contador del
  //    badge se actualice si el scheduler detecta nuevos candidatos sin que el
  //    usuario tenga que abrir el dropdown)
  useEffect(() => {
    loadPlans();
  }, [loadPlans]);

  useEffect(() => {
    const intervalMs = open ? 60000 : 300000;
    const id = setInterval(loadPlans, intervalMs);
    return () => clearInterval(id);
  }, [open, loadPlans]);

  // Al abrir el dropdown, forzar un refresh inmediato para no esperar al interval
  const toggleOpen = () => {
    setOpen((prev) => {
      const next = !prev;
      if (next) loadPlans();  // fresh data al abrir
      return next;
    });
  };

  const handleScanNow = async () => {
    setScanning(true);
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.post(`${API_URL}/emails/dispatch-plans/scan-now`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(`Escaneadas ${data.scanned} · Creadas ${data.created}`);
      await loadPlans();
    } catch (e) {
      toast.error(`Error al escanear: ${e.response?.data?.detail || e.message}`);
    } finally {
      setScanning(false);
    }
  };

  const handleDismiss = async (planId, evt) => {
    evt?.stopPropagation();
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/emails/dispatch-plans/${planId}/dismiss`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      await loadPlans();
    } catch (e) {
      toast.error(`Error al descartar: ${e.response?.data?.detail || e.message}`);
    }
  };

  const handlePlanClick = (plan) => {
    setSelectedPlan({
      id: plan.email_id,
      subject: plan.email_subject,
      from_email: plan.from_email,
    });
  };

  const handleApplied = async () => {
    await loadPlans();
    if (onPlanApplied) onPlanApplied();
  };

  return (
    <>
      <div className="relative">
        <Button
          variant="outline"
          size="sm"
          className="border-amber-600/50 text-amber-300 hover:bg-amber-900/20 relative"
          onClick={toggleOpen}
          data-testid="dispatch-plans-badge-btn"
          title={pendingCount > 0 ? `${pendingCount} email(s) pendientes de distribuir al evento` : 'Sin emails pendientes'}
        >
          <Inbox className="w-4 h-4 sm:mr-1" />
          <span className="hidden sm:inline">Distribuir</span>
          {pendingCount > 0 && (
            <span
              className="absolute -top-1 -right-1 bg-amber-500 text-slate-900 rounded-full text-xs font-bold w-5 h-5 flex items-center justify-center"
              data-testid="dispatch-plans-badge-count"
            >
              {pendingCount}
            </span>
          )}
        </Button>

        {open && (
          <div
            className="absolute top-full right-0 mt-2 w-96 max-h-[500px] overflow-y-auto bg-slate-900 border border-slate-700 rounded-lg shadow-2xl z-50"
            data-testid="dispatch-plans-dropdown"
          >
            <div className="flex items-center justify-between px-3 py-2 border-b border-slate-700 bg-slate-800/50">
              <h4 className="text-sm font-semibold text-white flex items-center gap-2">
                <Mail className="w-4 h-4 text-amber-400" />
                Pendientes ({pendingCount})
              </h4>
              <div className="flex gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleScanNow}
                  disabled={scanning}
                  className="text-slate-400 hover:text-white h-7 px-2"
                  data-testid="dispatch-plans-scan-btn"
                  title="Escanear emails recientes ahora"
                >
                  {scanning ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setOpen(false)}
                  className="text-slate-400 hover:text-white h-7 px-2"
                >
                  <X className="w-3 h-3" />
                </Button>
              </div>
            </div>

            {loading && plans.length === 0 && (
              <div className="text-center py-8 text-slate-400 text-sm">
                <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" />
                Cargando...
              </div>
            )}

            {!loading && plans.length === 0 && (
              <div className="text-center py-8 text-slate-400 text-sm">
                <Inbox className="w-8 h-8 mx-auto mb-2 opacity-40" />
                <p>Sin emails pendientes</p>
                <p className="text-xs text-slate-500 mt-1">
                  El scheduler revisa cada 15 min
                </p>
              </div>
            )}

            <div className="divide-y divide-slate-800">
              {plans.map((p) => (
                <div
                  key={p.email_id}
                  className="p-3 hover:bg-slate-800/60 cursor-pointer transition-colors"
                  onClick={() => handlePlanClick(p)}
                  data-testid={`dispatch-plan-${p.email_id}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-white truncate">
                        {p.email_subject}
                      </p>
                      <p className="text-xs text-slate-500 truncate mt-0.5">
                        {p.from_name || p.from_email}
                      </p>
                      <div className="flex gap-2 mt-1.5">
                        {p.has_attachment && (
                          <span className="px-1.5 py-0.5 bg-blue-500/20 text-blue-300 rounded text-xs flex items-center gap-1">
                            <Paperclip className="w-3 h-3" /> adjunto
                          </span>
                        )}
                        {p.has_music_link && (
                          <span className="px-1.5 py-0.5 bg-red-500/20 text-red-300 rounded text-xs flex items-center gap-1">
                            <Music className="w-3 h-3" /> música
                          </span>
                        )}
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => handleDismiss(p.email_id, e)}
                      className="text-slate-500 hover:text-red-400 h-6 w-6 p-0 shrink-0"
                      title="Descartar este plan"
                      data-testid={`dispatch-plan-dismiss-${p.email_id}`}
                    >
                      <X className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <EmailAIDispatchDialog
        isOpen={selectedPlan !== null}
        onClose={() => setSelectedPlan(null)}
        email={selectedPlan}
        onApplied={handleApplied}
      />
    </>
  );
};

export default DispatchPlansDropdown;
